// frontend/src/features/ups/ManageAgencies.js
// Phase 8: UPS-admin Agency Management table
//
// Purpose:
// - Acts as the *visual control plane* for agencies
// - Makes contract state + site permissions visible at a glance
// - Entry point into deeper agency management (drawer + modals)
//
// Key ideas:
// - This page is READ-heavy and MUST stay hook-safe
// - No useSelector / hooks inside table loops
// - Contract visibility here prevents "silent misconfiguration"

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  fetchAgencies,
  deleteAgency,
  selectAllAgencies,
} from '../agencies/agenciesSlice';

import { loadMyCompanySites, selectCompanySites } from '../sites/sitesSlice';
import { fetchContracts, selectContractsState } from '../contracts/contractsSlice';

import { MoreHorizontal, Plus, Search, ChevronRight } from 'lucide-react';

import CreateAgencyWizardModal from './modals/CreateAgencyWizardModal';
import AddAccountModal from './modals/AddAccountModal';
import ConfirmDeleteModal from './modals/ConfirmDeleteModal';
import AgencyDrawer from './modals/AgencyDrawer';


// ─────────────────────────────────────────────────────────────
// RowActionsMenu
// ─────────────────────────────────────────────────────────────
// File-local component to keep table rows clean.
// Handles:
// - Keyboard escape
// - Outside click dismissal
// - Focus management (a11y)
//
// IMPORTANT:
// This component is intentionally dumb:
// it receives callbacks and never touches Redux.
function RowActionsMenu({ onAdd, onDelete }) {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef(null);
  const firstItemRef = React.useRef(null);

  // Close menu on outside click / escape key
  React.useEffect(() => {
    const onDocClick = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    const onKeydown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKeydown);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKeydown);
    };
  }, []);

  // When menu opens, focus first item (keyboard users)
  React.useEffect(() => {
    if (open) firstItemRef.current?.focus();
  }, [open]);

  return (
    <div ref={rootRef} className="relative inline-block text-left">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center justify-center h-9 w-9 rounded hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <MoreHorizontal size={18} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-1 w-44 z-50 rounded-md border bg-white shadow-lg overflow-hidden"
        >
          <button
            ref={firstItemRef}
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onAdd?.();
            }}
            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 focus:outline-none"
          >
            Add account
          </button>

          <button
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onDelete?.();
            }}
            className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 focus:outline-none"
          >
            Delete agency
          </button>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// ManageAgencies (main page component)
// ─────────────────────────────────────────────────────────────
export default function ManageAgencies() {
  const dispatch = useDispatch();

  // Core data sources
  const agencies = useSelector(selectAllAgencies);
  const sites = useSelector(selectCompanySites);
  const contractsState = useSelector(selectContractsState);

  // Local UI state
  const loading = useSelector((s) => s.agencies.loading);
  const error = useSelector((s) => s.agencies.error);

  const [query, setQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [currentAgency, setCurrentAgency] = useState(null);

  // Initial data load
  //
  // NOTE:
  // - We intentionally load sites + contracts here so the table
  //   can render contract visibility without opening drawers.
  useEffect(() => {
    dispatch(fetchAgencies());
    dispatch(loadMyCompanySites());
    dispatch(fetchContracts());
  }, [dispatch]);

  // Search filter (pure, derived)
  const filtered = agencies.filter((a) =>
    a.name?.toLowerCase().includes(query.toLowerCase())
  );

  // ─────────────────────────────────────────────────────────────
  // Derived lookup structures (NO hooks inside render loops)
  // ─────────────────────────────────────────────────────────────

  // Map agencyId → contract
  //
  // WHY:
  // - Table renders many rows
  // - useSelector inside .map() would violate hook rules
  // - Map gives O(1) lookup and stable identity
  const contractByAgencyId = useMemo(() => {
    const m = new Map();
    const list = contractsState?.list || [];

    for (const c of list) {
      const agId = c.agency_id ?? c.agencyId;
      if (agId != null) m.set(Number(agId), c);
    }
    return m;
  }, [contractsState?.list]);

  const totalSites = Array.isArray(sites) ? sites.length : 0;

  // Contract badge semantics
  //
  // This communicates *permission state*, not business state.
  const getContractBadge = (contract) => {
    if (!contract) {
      return { label: 'No contract', cls: 'bg-gray-100 text-gray-700 border-gray-200' };
    }
    if ((contract.status || '').toLowerCase() === 'active') {
      return { label: 'Active', cls: 'bg-green-50 text-green-700 border-green-200' };
    }
    return { label: 'Disabled', cls: 'bg-amber-50 text-amber-700 border-amber-200' };
  };

  // Allowed sites count
  //
  // Priority order:
  // 1) Cached contract_sites (if drawer was opened before)
  // 2) Backend-provided allowed_sites_count (recommended)
  // 3) Fallback placeholder (? / total)
  const getAllowedSitesText = (contract) => {
    if (!contract) return '—';

    const byContractId = contractsState?.sitesByContractId || {};
    const rows = byContractId[String(contract.id)] || byContractId[contract.id];

    if (Array.isArray(rows)) {
      const allowed = rows.filter(
        (r) => (r.status || '').toLowerCase() === 'active'
      ).length;
      return `${allowed} / ${totalSites}`;
    }

    const allowedCount =
      contract.allowed_sites_count ??
      contract.allowedSitesCount ??
      null;

    if (typeof allowedCount === 'number') {
      return `${allowedCount} / ${totalSites}`;
    }

    return `? / ${totalSites}`;
  };

  // Row-level actions (kept explicit for clarity)
  const openDrawer = (ag) => {
    setCurrentAgency(ag);
    setDrawerOpen(true);
  };
  const openAdd = (ag) => {
    setCurrentAgency(ag);
    setAddOpen(true);
  };
  const openDelete = (ag) => {
    setCurrentAgency(ag);
    setDeleteOpen(true);
  };

  return (
    <>
      {/* Main page content */}
      <div className="space-y-6">
        {/* Header / Toolbar */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h1 className="text-2xl font-bold">Manage Agencies</h1>

          <div className="flex items-center gap-2">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2 top-2.5" size={16} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search agencies…"
                className="pl-8 pr-3 py-2 border rounded-md"
              />
            </div>

            {/* Create */}
            <button
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <Plus size={16} /> New agency
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow">
          {loading ? (
            <div className="p-6 text-gray-500">Loading…</div>
          ) : error ? (
            <div className="p-6 text-red-600">{error}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Contract
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Allowed sites
                    </th>
                    <th className="px-6 py-3" />
                  </tr>
                </thead>

                <tbody className="divide-y">
                  {filtered.map((ag) => {
                    const contract = contractByAgencyId.get(Number(ag.id));
                    const badge = getContractBadge(contract);
                    const allowedText = getAllowedSitesText(contract);

                    return (
                      <tr key={ag.id} className="hover:bg-gray-50">
                        <td className="px-6 py-3">{ag.id}</td>

                        <td className="px-6 py-3">
                          <button
                            onClick={() => openDrawer(ag)}
                            className="inline-flex items-center gap-1 text-blue-700 hover:underline"
                          >
                            {ag.name} <ChevronRight size={16} />
                          </button>
                        </td>

                        <td className="px-6 py-3">
                          <span
                            className={`inline-flex items-center px-2 py-1 text-xs rounded border ${badge.cls}`}
                          >
                            {badge.label}
                          </span>
                        </td>

                        <td className="px-6 py-3 text-sm text-gray-700">
                          {allowedText}
                        </td>

                        <td className="px-6 py-3">
                          <div className="flex justify-end">
                            <RowActionsMenu
                              onAdd={() => openAdd(ag)}
                              onDelete={() => openDelete(ag)}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                        No agencies found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modals / Drawers live OUTSIDE spacing stack */}
      {createOpen && (
        <CreateAgencyWizardModal
          isOpen={createOpen}
          onClose={() => setCreateOpen(false)}
        />
      )}

      {addOpen && currentAgency && (
        <AddAccountModal
          isOpen={addOpen}
          agency={currentAgency}
          onClose={() => {
            setAddOpen(false);
            setCurrentAgency(null);
          }}
        />
      )}

      {deleteOpen && currentAgency && (
        <ConfirmDeleteModal
          isOpen={deleteOpen}
          agency={currentAgency}
          onConfirm={async () => {
            await dispatch(deleteAgency(currentAgency.id));
            setDeleteOpen(false);
            setCurrentAgency(null);
          }}
          onClose={() => {
            setDeleteOpen(false);
            setCurrentAgency(null);
          }}
        />
      )}

      {drawerOpen && currentAgency && (
        <AgencyDrawer
          isOpen={drawerOpen}
          agency={currentAgency}
          onClose={() => setDrawerOpen(false)}
          onAddAccount={() => {
            setDrawerOpen(false);
            setAddOpen(true);
          }}
        />
      )}
    </>
  );
}
